import React from 'react'
import { Button, Dropdown as SemanticuiDropdown, Icon, Popup } from 'semantic-ui-react'
import styled from 'styled-components'
import dayjs from 'dayjs'

import useModal from 'hooks/useModal'
import { DatePicker } from 'components/DatePicker'
import TimePicker, { TimeZone } from 'components/TimePicker'

/**
 *
 * @param {object} props
 * @param {string} [props.className]
 * @param {(onSuccess: function) => (e, element) => void} props.createOnSendImmediately
 * @param {(onSuccess: function) => (e, element) => void} [props.createOnScheduleSend]
 * @param {boolean} [props.loading]
 * @param {boolean} [props.disabled]
 * @param {number} [props.numberOfRecipients]
 * @param {import('dayjs').Dayjs} props.scheduleDate
 * @param {function} props.setScheduleDate
 * @param {number} props.scheduleTime
 * @param {function} props.setScheduleTime
 * @param {'draft' | 'sent' | 'scheduled'} props.surveyState
 * @param {function} props.onCancelScheduled
 */
export default function OneoffEmailSendButton({
  className,
  createOnSendImmediately,
  createOnScheduleSend,
  scheduleDate,
  setScheduleDate,
  scheduleTime,
  setScheduleTime,
  loading,
  disabled,
  numberOfRecipients,
  surveyState,
  onCancelScheduled,
}) {
  const { Modal, open, close } = useModal()

  const hasSurveyState = ['draft', 'sent', 'scheduled'].includes(surveyState)
  if (!hasSurveyState) {
    return null
  }

  if (surveyState === 'scheduled') {
    return (
      <Button className={className} basic onClick={onCancelScheduled}>
        <Icon name="fal fa-stopwatch" /> Cancel schedule
      </Button>
    )
  }

  const disabledSendNow = surveyState === 'sent' || disabled
  if (disabledSendNow) {
    return (
      <Popup
        basic
        wide
        trigger={
          <Button.Group color="green" className={className}>
            <Button onClick={open} loading={loading} disabled>
              <Icon name="fal fa-paper-plane" />
              Send now
            </Button>
            <SendOptions
              loading={loading}
              disabled
              createOnScheduleSend={createOnScheduleSend}
              scheduleDate={scheduleDate}
              setScheduleDate={setScheduleDate}
              scheduleTime={scheduleTime}
              setScheduleTime={setScheduleTime}
            />
          </Button.Group>
        }
      >
        <React.Fragment>
          <p>Please correct the following before sending:</p>
          <ul>
            <li>Save latest changes</li>
            <li>Create at least one audience rule</li>
            <li>Ensure survey expiration settings are correct</li>
          </ul>
        </React.Fragment>
      </Popup>
    )
  }

  return (
    <React.Fragment>
      <Modal size="tiny">
        <Modal.Header>Send the email immediately</Modal.Header>
        <Modal.Content>
          You're about to send this email to {numberOfRecipients} customers. Do you wish to
          continue?
        </Modal.Content>
        <Modal.Actions>
          <Button
            primary
            onClick={() => createOnSendImmediately(close)}
            loading={loading}
            disabled={loading}
          >
            Send immediately
          </Button>
        </Modal.Actions>
      </Modal>
      <Button.Group color="green" className={className}>
        <Button onClick={open} loading={loading}>
          <Icon name="fal fa-paper-plane" />
          Send now
        </Button>
        <SendOptions
          loading={loading}
          createOnScheduleSend={createOnScheduleSend}
          scheduleDate={scheduleDate}
          setScheduleDate={setScheduleDate}
          scheduleTime={scheduleTime}
          setScheduleTime={setScheduleTime}
        />
      </Button.Group>
    </React.Fragment>
  )
}

/**
 *
 * @param {object} props
 * @param {boolean} props.loading
 * @param {boolean} [props.disabled]
 * @param {(onSuccess: function) => (e, element) => void} props.createOnScheduleSend
 * @param {import('dayjs').Dayjs} props.scheduleDate
 * @param {function} props.setScheduleDate
 * @param {number} props.scheduleTime
 * @param {function} props.setScheduleTime
 */
function SendOptions({
  loading,
  createOnScheduleSend,
  scheduleDate,
  setScheduleDate,
  setScheduleTime,
  scheduleTime,
  disabled,
}) {
  const { Modal, open, close } = useModal()
  const isScheduleDateMatchToday = !scheduleDate.isAfter(dayjs())
  return (
    <React.Fragment>
      <Modal size="tiny">
        <Modal.Header>Schedule email send</Modal.Header>
        <Modal.Content>
          <div className="flex">
            <DatePicker scheduleDate={scheduleDate} setScheduleDate={setScheduleDate} />
            <TimePicker
              className="ml2"
              setScheduleTime={setScheduleTime}
              scheduleTime={scheduleTime}
              isScheduleDateMatchToday={isScheduleDateMatchToday}
            />
          </div>
          <TimeZone />
        </Modal.Content>
        <Modal.Actions>
          <Button
            color="green"
            loading={loading}
            disabled={loading}
            onClick={() => createOnScheduleSend(close)}
          >
            Schedule
          </Button>
        </Modal.Actions>
      </Modal>

      <Dropdown
        icon="fas fa-caret-down"
        className="button icon"
        floating
        pointing="top right"
        disabled={disabled}
      >
        <Dropdown.Menu>
          <Dropdown.Item icon="fal fa-clock" text="Schedule send" onClick={open} />
        </Dropdown.Menu>
      </Dropdown>
    </React.Fragment>
  )
}

/** @type {typeof SemanticuiDropdown} */
const Dropdown = styled(SemanticuiDropdown)`
  && {
    border-left: 1px solid rgba(255, 255, 255, 0.5);
  }

  &&& > .menu .text {
    white-space: nowrap;
  }
`



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/OneoffEmailSendButton/index.js